import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import SendIcon from '@mui/icons-material/Send';
import { LoadingButton } from '@mui/lab';
import {
	Autocomplete,
	Box,
	Chip,
	CircularProgress,
	IconButton,
	Paper,
	Skeleton,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsMobile } from '../hooks';
import { useI18N } from '../i18n';
import { useRedux } from '../redux';
import { useAuth } from '../redux/slices/auth';
import { useDialog } from '../redux/slices/dialog';
import { useTheme } from '../redux/slices/theme';
import { Api } from '../services/Api';
import { FileDropArea } from './FileDropArea';
import { RichTextEditor } from './RichTextEditor';
import { TicketComment } from './TicketComment';
import { TicketStatus } from './TicketStatus';
import type { I18NKey } from '../i18n';
import type { DialogAction } from '../redux/slices/dialog';
import type { TicketCustomFieldData, NewTicketData } from '../redux/slices/tickets';
import { TicketCommentData } from '../redux/slices/comments';
import type { GetValuesFn } from './RichTextEditor';
import { useDispatch } from 'react-redux';
import {
	getTicketById,
	useTickets,
	updateTicket,
	FIXED_TICKET_STATES,
} from '../redux/slices/tickets';
import {
	getAttachmentsByTicketId,
	uploadAttachments,
	useAttachments,
} from '../redux/slices/attachments';
import type { Attachment } from '../redux/slices/attachments';
import { useComments, addCommentToTicket, getCommentByTicketId } from '../redux/slices/comments';
import { Timestamp } from 'firebase/firestore';
import { replyTicketTemplate } from '../templates/Email/replyTicketTemplate';
import filesToAttachments from '../functions/filesToAttachments';
import { getGroups, Group, useGroups } from '../redux/slices/groups';
import useGetGroupOfTicket from '../hooks/useGetGroupOfTicket';
import { WhatsApp } from '@mui/icons-material';
import UpdatePhoneModal from './UpdatePhoneModal';

export interface TicketViewProps {
	id?: string;
}

export interface TicketViewErrors {
	answerText: I18NKey;
	status: I18NKey;
}

export const TicketView = ({ id }: TicketViewProps): JSX.Element => {
	const i18n = useI18N();
	const isMobile = useIsMobile();
	const { dialog, snackbar, spinner } = useRedux();
	const { user, company, isTaugorCompany } = useAuth();
	const { action: dialogAction, data: dialogData } = useDialog();
	const { language } = useTheme();
	const dispatch = useDispatch();

	const { ticket, loading } = useTickets();
	const { attachmentByTicketId, loading: loadingAttachments } = useAttachments();
	const ticketAttachments = attachmentByTicketId[id ?? ''] ?? [];

	const { comments, loading: loadingComments } = useComments();
	const { group } = useGetGroupOfTicket(ticket?.group || '');

	const ticketStatusNames = FIXED_TICKET_STATES.map((status) => status.name);

	const [loadingSendEmail, setLoadingSendEmail] = useState(false);
	const [status, setStatus] = useState<string>();
	const [deadline, setDeadline] = useState<number | null>();
	const [isCanceling, setCanceling] = useState(false);
	const [isClosing, setClosing] = useState(false);
	const [isReopening, setReopening] = useState(false);
	const [isDownloadingAttachment, setDownloadingAttachment] = useState<boolean[]>([]);
	const [isSending, setSending] = useState(false);
	const [customFields, setCustomFields] = useState<TicketCustomFieldData[]>([]);
	const [showPhoneModal, setShowPhoneModal] = useState(false);

	const [answerTextRaw, setAnswerTextRaw] = useState('');
	const [product, setProduct] = useState('');
	const [files, setFiles] = useState<File[]>([]);
	const [errors, setErrors] = useState<TicketViewErrors>({
		answerText: '',
		status: '',
	});

	const getAnswerValuesRef = useRef<GetValuesFn>(null);

	const dateTimeFormatter = useMemo(
		() =>
			new Intl.DateTimeFormat(language, {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour: '2-digit',
				hour12: false,
				minute: '2-digit',
				second: '2-digit',
			}),
		[language]
	);

	const isOpen = useMemo(() => ticket?.status !== 'Finalizado', [ticket]);
	// const [readStatuses, setReadStatuses] = useState<{
	// 	[uid: string]: { viewed: boolean; viewedAt: string | null };
	// }>({});
	useEffect(() => {
		dispatch(getTicketById(id || ''));
		dispatch(getAttachmentsByTicketId(id || ''));
		dispatch(getCommentByTicketId(id || ''));
	}, [dispatch, id]);

	// useEffect(() => {
	// 	const loadStatuses = async () => {
	// 		const statuses: { [uid: string]: { viewed: boolean; viewedAt: string | null } } = {};
	// 		const ticketComments = comments[ticket?.ticketId ?? ''] ?? [];

	// 		await Promise.all(
	// 			ticketComments.map(async (comment) => {
	// 				if (comment.emailViewDetectorUid) {
	// 					const status = (await Api.tickets.getEmailReadStatus(
	// 						comment.emailViewDetectorUid
	// 					)) as any;
	// 					statuses[comment.emailViewDetectorUid] = {
	// 						viewed: status.viewed,
	// 						viewedAt: status.viewedAt,
	// 					};
	// 				}
	// 			})
	// 		);

	// 		setReadStatuses(statuses);
	// 	};

	// 	loadStatuses();
	// }, [comments]);

	const handleClose = useCallback(
		async (action: DialogAction, data: Record<string, unknown>) => {
			if (!id) {
				return;
			}

			if (action === 'cancel-ticket') {
				setCanceling(true);
			} else {
				setClosing(true);
			}

			spinner.show();
			try {
				const commentData: TicketCommentData = {
					user: { uid: user?.uid ?? '', name: user?.name ?? '' },
					content: [
						`<strong>Usuário ${
							action === 'cancel-ticket' ? 'Cancelou' : 'Encerrou'
						} a Solicitação:</strong>`,
						`Motivo do ${action === 'cancel-ticket' ? 'cancelamento' : 'encerramento'}: ${
							data.reason
						}`,
						`Descrição enviada por ele: ${data.reasonDetails}`,
					].join('<br/>'),
				};

				dispatch(addCommentToTicket({ ticketId: id, commentData }));

				setLoadingSendEmail(true);
				// Send email cancel ticket
				await Api.tickets.sendMail({
					to: ticket?.associatedUser?.email ? ticket?.associatedUser?.email : ticket?.email ?? '',
					cc: [group?.emailNotification ?? '', ...(ticket?.ccEmails ?? [])],
					subject: `Seu ticket #${ticket?.number} foi ${
						action === 'cancel-ticket' ? 'cancelado' : 'encerrado'
					}`,
					html: replyTicketTemplate(
						id,
						ticket?.number ?? '',
						user?.name ?? '',
						commentData.content,
						action
					),
					attachments: [],
					companyUid: company?.uid ?? '',
				});

				setLoadingSendEmail(false);

				// Atualiza o status do ticket
				dispatch(updateTicket({ ticketId: id, updateFields: { status: 'Finalizado' } }));

				snackbar.show({ status: 'success', message: 'tickets.view.success.close' });
			} catch (err) {
				snackbar.show({ status: 'error', message: 'tickets.view.errors.close' });
			} finally {
				spinner.hide();
			}

			if (action === 'cancel-ticket') {
				setCanceling(false);
			} else {
				setClosing(false);
			}
		},
		[id, user, ticket, group, company]
	);

	const handleReopen = useCallback(async () => {
		if (!id) {
			return;
		}

		setReopening(true);

		try {
			dispatch(updateTicket({ ticketId: id, updateFields: { status: 'Reaberto' } }));

			snackbar.show({ status: 'success', message: 'tickets.view.success.reopen' });
		} catch (err) {
			snackbar.show({ status: 'error', message: 'tickets.view.errors.reopen' });
		}

		setReopening(false);
	}, [id]);

	const handleDownloadAttachment = async (attachment: Attachment, i: number) => {
		setDownloadingAttachment((prevDownloadingAttachment) => {
			prevDownloadingAttachment[i] = true;

			return [...prevDownloadingAttachment];
		});

		try {
			const anchor = document.createElement('a');
			anchor.href = attachment.url;
			anchor.download = attachment.name;
			anchor.target = '_blank';
			document.body.appendChild(anchor);
			anchor.click();
			document.body.removeChild(anchor);

			// const response = await fetch(attachment.url);
			// const file = await response.blob();
			// const url = URL.createObjectURL(file);

			// const anchor = document.createElement('a');
			// anchor.style.display = 'none';
			// document.body.appendChild(anchor);
			// anchor.download = attachment.name;
			// anchor.href = url;
			// anchor.dispatchEvent(new MouseEvent('click'));
			// anchor.remove();

			// URL.revokeObjectURL(url);
		} catch (err) {
			snackbar.show({ status: 'error', message: 'tickets.view.errors.downloadAttachment' });
		}

		setDownloadingAttachment((prevDownloadingAttachment) => {
			prevDownloadingAttachment[i] = false;

			return [...prevDownloadingAttachment];
		});
	};

	const handleSend = useCallback(async () => {
		const newErrors: TicketViewErrors = {
			answerText: '',
			status: '',
		};
		let hasErrors = false;

		if (!id || !getAnswerValuesRef.current) {
			snackbar.show({ status: 'error', message: 'tickets.view.errors.send' });

			return;
		}

		const [answerTextRaw, answerText] = await getAnswerValuesRef.current();
		setAnswerTextRaw(answerTextRaw);

		if (!answerText || answerText === '<p></p>') {
			newErrors.answerText = 'tickets.view.errors.answer.empty';
			hasErrors = true;
		}

		if (hasErrors) {
			setErrors(newErrors);

			return;
		}

		setSending(true);
		spinner.show();

		try {
			setLoadingSendEmail(true);
			const participants = [...(ticket?.ccEmails ?? [])];
			if (!participants.includes(user?.email ?? '')) {
				participants.push(user?.email ?? '');
			}

			const attachments = await filesToAttachments(files);
			const responseEmail = (await Api.tickets.sendMail({
				to: ticket?.associatedUser?.email ? ticket?.associatedUser?.email : ticket?.email ?? '',
				cc: [
					group?.emailNotification ?? '',
					...participants.filter(
						(email) => email !== (ticket?.associatedUser?.email || ticket?.email)
					),
				],
				subject: `Nova resposta no ticket #${ticket?.number}`,
				attachments,
				html: replyTicketTemplate(id, ticket?.number ?? '', user?.name ?? '', answerText),
				companyUid: company?.uid ?? '',
			})) as any;

			const phoneNumbers: string[] = [
				...(group?.phoneNumbers ?? []),
				...(ticket?.phone ? [ticket.phone] : []),
			];

			if (phoneNumbers.length > 0) {
				await Api.tickets.sendWtsMessage({
					to: phoneNumbers ?? [],
					parameters: {
						Razao: ticket?.associatedUser?.razao
							? ticket?.associatedUser?.razao
							: ticket?.razao ?? '',
						cnpj: ticket?.associatedUser?.cnpj ? ticket?.associatedUser?.cnpj : ticket?.cnpj ?? '',
						'dono do ticket': ticket?.associatedUser?.name
							? ticket?.associatedUser?.name
							: ticket?.user?.name ?? '',
						categoria: ticket?.type ?? '',
						ticket: ticket?.number ?? '',
						link: `https://inbox.taugor.app/tickets/${id}`,
					},
				});
			}

			if (answerText) {
				const result = (await dispatch(
					addCommentToTicket({
						ticketId: id,
						commentData: {
							content: answerText,
							public: true,
							user: { uid: user?.uid ?? '', name: user?.name ?? '' },
							// emailViewDetectorUid: responseEmail?.viewDetectorUid ?? '',
						},
					})
				)) as any;

				const commentId = result.payload.commentId;

				if (files.length > 0) {
					dispatch(
						uploadAttachments({
							ticketId: id,
							files,
							commentId,
						})
					);
				}
			}

			setLoadingSendEmail(false);

			const updateFields: any = {
				lastChangeDate: Timestamp.now().toMillis(),
				ccEmails: participants,
			};

			if (!ticket?.responsible && user?.uid !== ticket?.uid) {
				updateFields.responsible = { uid: user?.uid ?? '', name: user?.name ?? '' };
			}

			dispatch(updateTicket({ ticketId: id, updateFields }));

			setAnswerTextRaw('');
			setFiles([]);
			setErrors((prevErrors) => ({
				...prevErrors,
				answerText: '',
			}));

			snackbar.show({ status: 'success', message: 'tickets.view.success.send' });
		} catch (err) {
			snackbar.show({ status: 'error', message: 'tickets.view.errors.send' });
		} finally {
			spinner.hide();
		}

		setSending(false);
	}, [id, files, ticket, group, user, company]);

	const handleUpdateTicket = () => {
		if (!id) return;

		const updateFields: Partial<NewTicketData> = {};
		if (status) updateFields.status = status;
		if (deadline) updateFields.deadline = deadline;

		try {
			dispatch(
				updateTicket({
					ticketId: id,
					updateFields,
				})
			);

			snackbar.show({ status: 'success', message: 'tickets.view.success.update' });
		} catch (error) {
			snackbar.show({ status: 'error', message: 'tickets.view.errors.update' });
		}
	};

	useEffect(() => {
		const productField = customFields.find(
			(customField) => customField.question === 'Produto ou Serviços'
		);
		setProduct(productField?.answer ?? '');
	}, [customFields]);

	useEffect(() => {
		if (
			(dialogAction === 'cancel-ticket' || dialogAction === 'close-ticket') &&
			dialogData.reason &&
			dialogData.reasonDetails
		) {
			void handleClose(dialogAction, dialogData);
			dialog.update({ data: {} });
		}
	}, [dialogAction, dialogData]);

	return loading || ticket ? (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				gap: 2,
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 2,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						gap: 1,
						minWidth: 0,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							flexWrap: 'wrap',
							gap: isMobile ? 0.5 : 1,
						}}
					>
						<Tooltip title={i18n('tickets.view.status')}>
							{ticket && loading !== true ? (
								<TicketStatus ticket={ticket} isChip />
							) : (
								<Chip label={<Skeleton variant="text" width={50} />} />
							)}
						</Tooltip>

						<Tooltip title={i18n('tickets.view.number')}>
							<Chip
								label={
									ticket && loading !== true ? (
										ticket.number
									) : (
										<Skeleton variant="text" width={50} />
									)
								}
							/>
						</Tooltip>

						<Tooltip title={i18n('tickets.view.group')}>
							<Chip
								label={
									ticket && loading !== true && group ? (
										group.name
									) : (
										<Skeleton variant="text" width={50} />
									)
								}
							/>
						</Tooltip>
					</Box>

					<Typography variant="h5" noWrap>
						{ticket && loading !== true ? (
							ticket.title
						) : (
							<Skeleton variant="text" width={isMobile ? 200 : 300} />
						)}
					</Typography>

					{ticket && loading !== true ? (
						<>
							{product && (
								<Typography variant="caption" noWrap>
									{i18n('tickets.view.product')}:{' '}
									<Box
										component="span"
										sx={{
											fontWeight: 'bold',
										}}
									>
										{product}
									</Box>
								</Typography>
							)}

							<Typography
								variant="caption"
								noWrap
								display={'flex'}
								flexDirection={'row'}
								alignItems={'center'}
								gap={1}
							>
								{i18n('tickets.view.createdBy')}:{' '}
								<Box
									component="span"
									sx={{
										fontWeight: 'bold',
									}}
								>
									{ticket?.user?.name ?? '-'}
								</Box>
								{ticket?.phone && (
									<Tooltip title={i18n('tickets.list.whatsappPhoneTicket')}>
										<IconButton
											color="default"
											sx={{ backgroundColor: '#25D366' }}
											size="small"
											onClick={() => setShowPhoneModal(true)}
										>
											<WhatsApp />
										</IconButton>
									</Tooltip>
								)}
								<UpdatePhoneModal
									open={showPhoneModal}
									onClose={() => setShowPhoneModal(false)}
									ticket={ticket}
								/>
							</Typography>

							<Typography variant="caption" noWrap>
								{i18n('tickets.view.answeredBy')}:{' '}
								<Box
									component="span"
									sx={{
										fontWeight: 'bold',
									}}
								>
									{ticket.responsible?.name ?? '-'}
								</Box>
							</Typography>

							<Typography variant="caption" noWrap>
								{i18n('tickets.view.lastUpdate')}:{' '}
								<Box
									component="span"
									sx={{
										fontWeight: 'bold',
									}}
								>
									{ticket.lastChangeDate &&
										dateTimeFormatter.format(new Date(ticket.lastChangeDate))}
								</Box>
							</Typography>

							<Typography variant="caption" noWrap>
								{i18n('tickets.view.deadline')}:{' '}
								<Box
									component="span"
									sx={{
										fontWeight: 'bold',
									}}
								>
									{ticket.deadline ? dateTimeFormatter.format(new Date(ticket.deadline)) : '-'}
								</Box>
							</Typography>
						</>
					) : (
						<Typography variant="caption">
							<Skeleton variant="text" width={150} />
						</Typography>
					)}
				</Box>

				{ticket &&
					(isOpen ? (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								gap: 1,
							}}
						>
							<Tooltip title={isMobile ? i18n('tickets.view.cancel') : ''}>
								<LoadingButton
									variant="contained"
									color="error"
									startIcon={<CloseIcon />}
									disabled={isClosing || isSending || loadingSendEmail}
									loading={isCanceling}
									loadingPosition="start"
									sx={
										isMobile
											? {
													width: 40,
													height: 40,
													minWidth: 40,
													p: 0,
													borderRadius: '50%',

													'.MuiButton-startIcon': {
														m: 0,
													},
											  }
											: {}
									}
									onClick={() => dialog.show({ action: 'cancel-ticket' })}
								>
									{isMobile ? '' : i18n('tickets.view.cancel')}
								</LoadingButton>
							</Tooltip>

							<Tooltip title={isMobile ? i18n('tickets.view.close') : ''}>
								<LoadingButton
									variant="contained"
									color="success"
									startIcon={<CheckIcon />}
									disabled={isCanceling || isSending || loadingSendEmail}
									loading={isClosing}
									loadingPosition="start"
									sx={
										isMobile
											? {
													width: 40,
													height: 40,
													minWidth: 40,
													p: 0,
													borderRadius: '50%',

													'.MuiButton-startIcon': {
														m: 0,
													},
											  }
											: {}
									}
									onClick={() => dialog.show({ action: 'close-ticket' })}
								>
									{isMobile ? '' : i18n('tickets.view.close')}
								</LoadingButton>
							</Tooltip>

							<Box
								sx={
									isMobile
										? { display: 'flex', flexDirection: 'column', gap: 1, width: '100px' }
										: { display: 'flex', flexDirection: 'column', gap: 1 }
								}
							>
								<Typography variant="caption">{i18n('tickets.form.labelAdm')}: </Typography>
								<Autocomplete
									options={ticketStatusNames}
									value={status || ticket.status || ''}
									disabled={!isTaugorCompany}
									disablePortal
									openOnFocus
									renderInput={(params) => (
										<TextField
											{...params}
											label={i18n('tickets.view.status')}
											required
											error={!!errors.status}
											helperText={errors.status ? i18n(errors.status) : ''}
										/>
									)}
									sx={isMobile ? { width: '100%' } : {}}
									onChange={(e, newValue) => {
										if (!isTaugorCompany) return;

										setStatus(newValue || '');

										if (errors.status) {
											setErrors((prevErrors) => ({
												...prevErrors,
												group: '',
											}));
										}
									}}
								/>

								<TextField
									type="datetime-local"
									label={i18n('tickets.view.deadline')}
									value={
										deadline
											? new Date(deadline - new Date().getTimezoneOffset() * 60000)
													.toISOString()
													.slice(0, 16)
											: ticket.deadline
											? new Date(ticket.deadline - new Date().getTimezoneOffset() * 60000)
													.toISOString()
													.slice(0, 16)
											: ''
									}
									disabled={!isTaugorCompany}
									sx={isMobile ? { width: '100%' } : {}}
									onChange={(e) => {
										const date = Timestamp.fromDate(new Date(e.target.value));
										setDeadline(date.toMillis());
									}}
								/>

								<Tooltip
									title={
										!isTaugorCompany
											? i18n('tickets.form.cannotUpdate')
											: i18n('tickets.form.update')
									}
								>
									<Box>
										<LoadingButton
											variant="contained"
											loading={loading}
											disabled={!isTaugorCompany}
											sx={isMobile ? { width: '100%' } : {}}
											onClick={handleUpdateTicket}
										>
											{isMobile ? <SendIcon /> : i18n('tickets.form.update')}
										</LoadingButton>
									</Box>
								</Tooltip>
							</Box>
						</Box>
					) : (
						<Tooltip title={isMobile ? i18n('tickets.view.reopen') : ''}>
							<LoadingButton
								variant="contained"
								color="success"
								startIcon={<RotateRightIcon />}
								disabled={isSending}
								loading={isReopening}
								loadingPosition="start"
								sx={
									isMobile
										? {
												width: 40,
												height: 40,
												minWidth: 40,
												p: 0,
												borderRadius: '50%',

												'.MuiButton-startIcon': {
													m: 0,
												},
										  }
										: {}
								}
								onClick={handleReopen}
							>
								{isMobile ? '' : i18n('tickets.view.reopen')}
							</LoadingButton>
						</Tooltip>
					))}
			</Box>

			{ticket && loading !== true ? (
				<TicketComment
					comment={{
						id: `__intro-${ticket.ticketId}`,
						date: ticket.creationDate,
						user: ticket.user,
						content: ticket.description,
						public: true,
					}}
				/>
			) : (
				<TicketComment comment={null} />
			)}

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'flex-start',
					gap: 2,
					ml: isMobile ? 4 : 8,
				}}
			>
				<Typography variant="h6">{i18n('tickets.view.attachments')}</Typography>

				{loadingAttachments ? (
					new Array(2).fill(null).map((ticketAttachments, i) => (
						<Chip
							key={i}
							label={
								<Typography variant="inherit">
									<Skeleton variant="text" width={100} />
								</Typography>
							}
							icon={<AttachFileIcon />}
							deleteIcon={<CloudDownloadIcon />}
							onDelete={() => {
								// Do nothing
							}}
						/>
					))
				) : ticketAttachments.length > 0 ? (
					ticketAttachments.map((attachment, i) => (
						<Chip
							key={attachment.id}
							label={
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1,
									}}
								>
									<Typography variant="inherit" noWrap>
										{attachment.name}
									</Typography>

									<Typography variant="inherit" fontWeight="bold">
										({attachment.humanReadableLength})
									</Typography>
								</Box>
							}
							icon={<AttachFileIcon />}
							deleteIcon={
								isDownloadingAttachment[i] ? (
									<CircularProgress color="inherit" size="1rem" />
								) : (
									<CloudDownloadIcon />
								)
							}
							disabled={isDownloadingAttachment[i]}
							sx={{
								width: 'unset',
								maxWidth: isMobile ? 250 : 300,
							}}
							onDelete={() => handleDownloadAttachment(attachment, i)}
						/>
					))
				) : (
					<Typography>{i18n('tickets.view.noAttachmentsFound')}</Typography>
				)}

				<Typography variant="h6">{i18n('tickets.view.answers')}</Typography>

				{loadingComments && loadingAttachments ? (
					new Array(2).fill(null).map((_, i) => <TicketComment key={i} comment={null} />)
				) : id && comments[id]?.length > 0 ? (
					comments[id].map((comment) => (
						<TicketComment
							key={comment.id}
							comment={comment}
							handleDownloadAttachment={handleDownloadAttachment}
							isDownloadingAttachment={isDownloadingAttachment}
							// readStatus={
							// 	comment.emailViewDetectorUid ? readStatuses[comment.emailViewDetectorUid] : null
							// }
						/>
					))
				) : (
					<Typography>{i18n('tickets.view.noAnswersFound')}</Typography>
				)}

				{isOpen && (
					<Paper
						elevation={6}
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'flex-end',
							gap: 2,
							width: 1,
							p: isMobile ? 1 : 2,
						}}
					>
						<RichTextEditor
							id="answer"
							label="tickets.view.answer"
							defaultValue={answerTextRaw}
							isDisabled={loading || loadingAttachments || loadingComments || loadingSendEmail}
							errorMessage={errors.answerText}
							getValuesRef={getAnswerValuesRef}
							onFocus={() => {
								if (errors.answerText) {
									setErrors((prevErrors) => ({
										...prevErrors,
										answerText: '',
									}));
								}
							}}
						/>

						<FileDropArea
							isDisabled={loading || loadingAttachments || loadingComments || loadingSendEmail}
							files={files}
							setFiles={setFiles}
						/>

						<LoadingButton
							variant="contained"
							startIcon={<SendIcon />}
							disabled={loading || loadingAttachments || loadingComments || loadingSendEmail}
							loading={isSending}
							loadingPosition="start"
							onClick={handleSend}
						>
							{i18n('tickets.view.send')}
						</LoadingButton>
					</Paper>
				)}
			</Box>
		</Box>
	) : (
		<Typography textAlign="center">{i18n('tickets.view.notFound')}</Typography>
	);
};
